import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useAuth } from '../../contexts/AuthContext';
import { useBackground } from '../../contexts/BackgroundContext';
import { useNavigationController } from '../../hooks/useNavigationController';
import { usePanelImages } from '../../hooks/usePanelImages';
import { HeaderPanel } from './HeaderPanel';
import { SectionContent } from './SectionContent';
import { MOBILE_BREAKPOINT, SECTION_KEYS } from './constants';

export interface AttendeeMenuShellProps {
  children: React.ReactNode;
}

/**
 * AttendeeMenuShell — Top-level layout wrapper that conditionally renders
 * the mobile swipeable header panel navigation for Attendee users on viewports
 * less than 768px, or falls back to rendering children (standard AppLayout)
 * for desktop viewports or non-Attendee roles.
 *
 * On viewport threshold crossing (resize/orientation change), the layout
 * switches without a page reload, preserving the current section index.
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4, 3.10
 */
export const AttendeeMenuShell: React.FC<AttendeeMenuShellProps> = ({ children }) => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT); // true when viewport ≤ 768px
  const { user, mustChangePassword } = useAuth();
  const isAttendee = user?.role === 'Attendee';
  const location = useLocation();

  const {
    currentIndex,
    goNext,
    goPrev,
    goTo,
    containerRef,
    sectionName,
    isFirst,
    isLast,
  } = useNavigationController();

  const { panelImages } = usePanelImages();
  const { backgroundUrl } = useBackground();

  // Resolve the image URL for the current section
  const currentSectionKey = SECTION_KEYS[currentIndex];
  const currentImageUrl = panelImages[currentSectionKey] ?? '';

  // Routes that should always pass through without the mobile shell
  const passthroughRoutes = ['/login', '/register', '/change-password', '/get-credentials', '/privacy', '/story-builder/interview', '/story-builder/review', '/booklet-browser', '/booklet-advertisers', '/booklet-memorial-tiles', '/guest-questions', '/presenter-portal'];
  const isPassthroughRoute = passthroughRoutes.some(r => location.pathname.startsWith(r));

  // Debug: log the shell decision factors
  if (typeof window !== 'undefined' && (window as any).__SHELL_DEBUG) {
    console.log('[AttendeeMenuShell]', { isMobile, isAttendee, role: user?.role, mustChangePassword, pathname: location.pathname, isPassthroughRoute });
  }

  // Desktop OR non-Attendee: render children (existing AppLayout passes through)
  // Also pass through when mustChangePassword is set (Req 1.3) or on public/auth routes
  if (!isMobile || !isAttendee || mustChangePassword || isPassthroughRoute) {
    return <>{children}</>;
  }

  // Mobile + Attendee: render swipeable header panel + content body
  return (
    <div style={styles.shell}>
      <HeaderPanel
        currentIndex={currentIndex}
        sectionName={sectionName}
        imageUrl={currentImageUrl}
        isFirst={isFirst}
        isLast={isLast}
        onSwipeLeft={goNext}
        onSwipeRight={goPrev}
        onDotClick={goTo}
        containerRef={containerRef}
      />

      {/* Content body — keyed by section index to force unmount/remount on section change */}
      <div
        key={currentIndex}
        style={{
          ...styles.contentBody,
          ...(backgroundUrl
            ? {
                backgroundImage: `url(${backgroundUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
              }
            : {}),
        }}
      >
        <Suspense fallback={<SectionLoadingFallback />}>
          <SectionContent currentIndex={currentIndex} />
        </Suspense>
      </div>

      {/* Persistent bottom navigation bar */}
      {/* Removed from AttendeeMenuShell — rendered at App level instead */}
    </div>
  );
};

/** Simple loading spinner shown while lazy-loaded page components are being fetched */
const SectionLoadingFallback: React.FC = () => (
  <div style={styles.loadingFallback}>
    <span>Loading…</span>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  shell: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 64px)', // subtract bottom nav height
    width: '100vw',
    maxWidth: '100vw',
    overflow: 'hidden',
  },
  contentBody: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100vw',
  },
  loadingFallback: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
    color: '#6b7280',
    fontSize: '0.95rem',
  },
};

export default AttendeeMenuShell;
